/**
 * Registra i tipi entita' del core nell'EntityRegistry.
 * Chiamato al boot DOPO loadPlugins().
 *
 * Quali entita' sono **bersagli** relazionali (= compaiono come pannello nei
 * detail altrui e nel picker "Associa ai record") non e' scritto qui in prosa
 * ma dichiarato dal flag `relationTarget` sul singolo descriptor: oggi solo
 * Person e Business. Le altre partecipano comunque alle Relationship come
 * *sorgenti*, che non richiede alcun flag.
 *
 * NOTA: i resolveLabel e search usano import dinamici dei moduli api
 * per evitare cicli di import al boot e per limitare il bundle iniziale
 * (l'api di un'entita' viene caricato solo quando serve).
 *
 * `search` ritorna `Page<T>` completa: il consumer (AddRelationDrawer,
 * TaskForm, TopBar) usa `items` per la lista e `totalCount`/`lastPage`
 * per decidere se caricare altre pagine (infinite scroll / load-more).
 */

import type { Page } from '@crm/shared';
import { registerEntityType } from './entityRegistry';
// Import statico e non dinamico: la sezione `board` fa parte del descrittore, che si costruisce al
// momento della registrazione. Al loro interno le api restano a caricamento differito.
import { businessBoard } from '../entities/business/businessBoard';
import { personBoard } from '../entities/person/personBoard';
import { quotationBoard } from '../entities/quotation/quotationBoard';
import { taskBoard } from '../entities/task/taskBoard';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Coercion al `Page<Record<string, unknown>>` atteso dal contratto
 * `EntityTypeDescriptor.search`. I `*Api.getAll` ritornano `Page<T>`
 * dove T è l'entity tipata; il registry lavora con shape opaca per
 * essere generico tra entità. Il doppio cast è un escape-hatch
 * documentato — concentrato qui invece che ripetuto su ogni search.
 */
function coercePageItems<T>(page: Page<T>): Page<Record<string, unknown>> {
  return { ...page, items: page.items as unknown as Record<string, unknown>[] };
}

/**
 * `resolveDetailPath` delle attività (FILO-26): non hanno una pagina propria, quindi si porta
 * l'utente sulla scheda del record collegato, al tab del tipo. Vedi activityParentPath.
 */
function activityParentPath(fqn: string) {
  return async (
    id: number,
    groupId: number,
    disabledTypes?: ReadonlySet<string>,
  ): Promise<string | undefined> => {
    const { resolveActivityParentPath } = await import('./activityParentPath');
    return resolveActivityParentPath(fqn, id, groupId, disabledTypes);
  };
}

/** Rimuove i tag HTML da una stringa (per ricavare un'etichetta testuale dal
 *  message WYSIWYG del LinkedinPost). */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\u00A0/g, ' ').trim();
}

export function registerCoreEntityTypes(): void {
  registerEntityType({
    typeName: 'Person',
    fqn: 'it.dvel.crm.core.model.Person',
    labelKey: 'person.label',
    labelPluralKey: 'nav.contacts',
    relationTarget: true,
    resolveLabel: async (id) => {
      const { personApi } = await import('../entities/person/personApi');
      const p = await personApi.getById(id);
      return `${p.firstName} ${p.lastName}`;
    },
    getLabelFromPayload: (p) => `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
    detailPath: (id) => `/contacts/${id}`,
    // TS-117: il Contatto sa disegnarsi in bacheca — la pipeline dei lead.
    board: personBoard,
    search: async (query, groupId, options) => {
      const { personApi } = await import('../entities/person/personApi');
      const page = await personApi.getAll(groupId, {
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'person',
  });

  registerEntityType({
    typeName: 'Business',
    fqn: 'it.dvel.crm.core.model.Business',
    labelKey: 'business.label',
    labelPluralKey: 'nav.companies',
    relationTarget: true,
    resolveLabel: async (id) => {
      const { businessApi } = await import('../entities/business/businessApi');
      const b = await businessApi.getById(id);
      return b.name;
    },
    getLabelFromPayload: (b) => String(b.name ?? ''),
    detailPath: (id) => `/companies/${id}`,
    // TS-117: l'Azienda sa disegnarsi in bacheca — la pipeline di acquisizione.
    board: businessBoard,
    search: async (query, groupId, options) => {
      const { businessApi } = await import('../entities/business/businessApi');
      const page = await businessApi.getAll(groupId, {
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'business',
  });

  // TS-87 — Il preventivo diventa entità di prodotto. Prima lo registrava il
  // plugin trentino-quotation, col FQN del proprio modello: da qui la
  // registrazione è del core e il FQN è quello del modello di prodotto.
  //
  // Il momento dello scambio conta. `registerEntityType` LANCIA su chiave
  // duplicata (non sovrascrive) e `main.tsx` registra i plugin PRIMA del core:
  // finché il plugin dichiara anche lui `typeName: 'Quotation'`,
  // l'applicazione non arriva al primo render. Le due modifiche vanno
  // rilasciate insieme - prima il submodule, poi questo.
  registerEntityType({
    typeName: 'Quotation',
    fqn: 'it.dvel.crm.core.model.Quotation',
    labelKey: 'quotation.label',
    labelPluralKey: 'nav.quotations',
    // Bersaglio relazionale: compare come pannello nei dettagli di contatto e
    // azienda, e nel picker "Associa ai record".
    relationTarget: true,
    resolveLabel: async (id) => {
      const { quotationApi } = await import('../entities/quotation/quotationApi');
      const q = await quotationApi.getById(id);
      return q.title || `Preventivo #${id}`;
    },
    getLabelFromPayload: (q) => {
      const label = (q.title ?? q.externalCode) as string | undefined;
      const id = q.quotationId as number | undefined;
      return label || (id != null ? `Preventivo #${id}` : '');
    },
    detailPath: (id) => `/quotations/${id}`,
    // TS-117: il Preventivo sa disegnarsi in bacheca. È stato il primo; oggi i tipi proponibili
    // quando si crea una pipeline sono tutti quelli che espongono `board` — li raccoglie
    // `pipelineCapableTypes()`, che filtra i descrittori, non un elenco scritto a mano.
    board: quotationBoard,
    search: async (query, groupId, options) => {
      const { quotationApi } = await import('../entities/quotation/quotationApi');
      const page = await quotationApi.getAll(groupId, {
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    // TS-18 — "Crea preventivo dal contatto/azienda". Da un contatto si crea e
    // basta: un preventivo esistente appartiene già a un'azienda, e
    // agganciarlo a un contatto qualsiasi non è un gesto che abbia senso
    // offrire. Da un'azienda invece il collegamento a uno esistente resta.
    createFromSource: (source) => {
      if (source.typeName === 'Person')
        return { url: `/quotations/new?personId=${source.classPK}`, hideLinkExisting: true };
      if (source.typeName === 'Business')
        return { url: `/quotations/new?businessId=${source.classPK}`, hideLinkExisting: false };
      return undefined;
    },
    icon: 'receipt',
  });

  registerEntityType({
    typeName: 'Task',
    fqn: 'it.dvel.crm.core.model.Task',
    labelKey: 'task.label',
    labelPluralKey: 'nav.tasks',
    resolveLabel: async (id) => {
      const { taskApi } = await import('../entities/task/taskApi');
      const t = await taskApi.getById(id);
      return t.title;
    },
    getLabelFromPayload: (t) => String(t.title ?? ''),
    detailPath: () => `/tasks`,
    board: taskBoard,
    search: async (query, groupId, options) => {
      const { taskApi } = await import('../entities/task/taskApi');
      const page = await taskApi.getAll(groupId, {
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'task',
  });

  registerEntityType({
    typeName: 'Message',
    fqn: 'it.dvel.crm.core.model.Message',
    labelKey: 'message.label',
    labelPluralKey: 'nav.messages',
    resolveLabel: async (id) => {
      const { messageApi } = await import('../entities/message/messageApi');
      const m = await messageApi.getById(id);
      return m.subject;
    },
    getLabelFromPayload: (m) => String(m.subject ?? ''),
    detailPath: (id) => `/inbox?messageId=${id}`,
    // LIMITAZIONE NOTA (decisione 2026-06-09): questo search (usato dal picker
    // entità di AddRelationDrawer / useAssociatedRecordsPicker per collegare un
    // Message da un'altra entità) NON passa `direction`, quindi messageApi.getAll
    // applica il default IN: il picker trova SOLO i messaggi ricevuti, mai gli
    // inviati (OUT). Scelta voluta perché il backend richiede direction come path
    // param e qui non c'è un "tab corrente" da cui ereditarla. Se in futuro serve
    // collegare anche i messaggi inviati, fare due fetch (IN + OUT) e mergere i
    // risultati, oppure aggiungere un sentinel "all" lato backend.
    search: async (query, groupId, options) => {
      const { messageApi } = await import('../entities/message/messageApi');
      const page = await messageApi.getAll(groupId, {
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'email',
  });

  registerEntityType({
    typeName: 'Meeting',
    fqn: 'it.dvel.crm.core.model.Meeting',
    labelKey: 'meeting.label',
    labelPluralKey: 'meeting.labelPlural',
    resolveLabel: async (id) => {
      const { meetingApi } = await import('../entities/meeting/meetingApi');
      const m = await meetingApi.getById(id);
      return m.description.slice(0, 60);
    },
    getLabelFromPayload: (m) => String(m.description ?? '').slice(0, 60),
    detailPath: () => `/meetings`,
    resolveDetailPath: activityParentPath('it.dvel.crm.core.model.Meeting'),
    search: async (query, groupId, options) => {
      const { meetingApi } = await import('../entities/meeting/meetingApi');
      const page = await meetingApi.getAll(groupId, {
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'event',
  });

  registerEntityType({
    typeName: 'Call',
    fqn: 'it.dvel.crm.core.model.Call',
    labelKey: 'call.label',
    labelPluralKey: 'call.labelPlural',
    resolveLabel: async (id) => {
      const { callApi } = await import('../entities/call/callApi');
      const c = await callApi.getById(id);
      return c.description.slice(0, 60);
    },
    getLabelFromPayload: (c) => String(c.description ?? '').slice(0, 60),
    detailPath: () => `/calls`,
    resolveDetailPath: activityParentPath('it.dvel.crm.core.model.Call'),
    // LIMITAZIONE NOTA: il backend richiede direction nel path (vedi
    // CallsResourceImpl.java); il picker AddRelationDrawer non e' a
    // conoscenza della direzione corrente, quindi il search applica
    // 'OUT' di default (caso piu' frequente di scelta nel CRM). Per
    // cercare le IN, aprire il list di Call e cambiare il filtro a mano
    // — il picker polimorfico resta limitato.
    search: async (query, groupId, options) => {
      const { callApi } = await import('../entities/call/callApi');
      const page = await callApi.getAll(groupId, {
        direction: 'OUT',
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'phone',
  });

  registerEntityType({
    typeName: 'Email',
    fqn: 'it.dvel.crm.core.model.Email',
    labelKey: 'email.label',
    labelPluralKey: 'email.labelPlural',
    resolveLabel: async (id) => {
      const { emailApi } = await import('../entities/email/emailApi');
      const e = await emailApi.getById(id);
      return e.description.slice(0, 60);
    },
    getLabelFromPayload: (e) => String(e.description ?? '').slice(0, 60),
    detailPath: () => `/emails`,
    resolveDetailPath: activityParentPath('it.dvel.crm.core.model.Email'),
    // LIMITAZIONE NOTA: come Call, il backend richiede direction nel path
    // e il picker AddRelationDrawer non la conosce → default 'OUT'.
    search: async (query, groupId, options) => {
      const { emailApi } = await import('../entities/email/emailApi');
      const page = await emailApi.getAll(groupId, {
        direction: 'OUT',
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'email',
  });

  registerEntityType({
    typeName: 'Note',
    fqn: 'it.dvel.crm.core.model.Note',
    labelKey: 'note.label',
    labelPluralKey: 'note.labelPlural',
    resolveLabel: async (id) => {
      const { noteApi } = await import('../entities/note/noteApi');
      const n = await noteApi.getById(id);
      return n.title;
    },
    getLabelFromPayload: (n) => String(n.title ?? ''),
    detailPath: () => `/notes`,
    resolveDetailPath: activityParentPath('it.dvel.crm.core.model.Note'),
    search: async (query, groupId, options) => {
      const { noteApi } = await import('../entities/note/noteApi');
      const page = await noteApi.getAll(groupId, {
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'note',
  });

  registerEntityType({
    typeName: 'LinkedinPost',
    fqn: 'it.dvel.crm.core.model.LinkedinPost',
    labelKey: 'linkedin.label',
    labelPluralKey: 'linkedin.labelPlural',
    resolveLabel: async (id) => {
      const { linkedinPostApi } = await import('../entities/linkedinPost/linkedinPostApi');
      const l = await linkedinPostApi.getById(id);
      return stripHtml(l.message).slice(0, 60);
    },
    getLabelFromPayload: (l) => stripHtml(String(l.message ?? '')).slice(0, 60),
    detailPath: () => `/linkedin-posts`,
    resolveDetailPath: activityParentPath('it.dvel.crm.core.model.LinkedinPost'),
    search: async (query, groupId, options) => {
      const { linkedinPostApi } = await import('../entities/linkedinPost/linkedinPostApi');
      const page = await linkedinPostApi.getAll(groupId, {
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'linkedin',
  });

  registerEntityType({
    typeName: 'WhatsAppMessage',
    fqn: 'it.dvel.crm.core.model.WhatsAppMessage',
    labelKey: 'whatsapp.label',
    labelPluralKey: 'whatsapp.labelPlural',
    resolveLabel: async (id) => {
      const { whatsAppMessageApi } = await import('../entities/whatsAppMessage/whatsAppMessageApi');
      const m = await whatsAppMessageApi.getById(id);
      return m.body.slice(0, 60);
    },
    getLabelFromPayload: (m) => String(m.body ?? '').slice(0, 60),
    // Nessuna pagina dedicata: i messaggi WhatsApp vivono nella timeline della
    // scheda contatto (RelationPanel), non c'è un list route standalone.
    detailPath: () => `/`,
    resolveDetailPath: activityParentPath('it.dvel.crm.core.model.WhatsAppMessage'),
    search: async (query, groupId, options) => {
      const { whatsAppMessageApi } = await import('../entities/whatsAppMessage/whatsAppMessageApi');
      const page = await whatsAppMessageApi.getAll(groupId, {
        search: query || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      });
      return coercePageItems(page);
    },
    icon: 'whatsapp',
  });

  registerEntityType({
    typeName: 'Queue',
    fqn: 'it.dvel.crm.core.model.Queue',
    labelKey: 'queue.label',
    labelPluralKey: 'nav.queues',
    resolveLabel: async (id) => {
      const { queueApi } = await import('../entities/queue/queueApi');
      const q = await queueApi.getById(id);
      return q.name;
    },
    getLabelFromPayload: (q) => String(q.name ?? ''),
    detailPath: () => `/settings/queues`,
    // Nessun search: il picker AddRelationDrawer non usa Queue come
    // target di relazioni.
    icon: 'queue',
  });
}
